import { FormEvent, useCallback, useEffect, useId, useState } from 'react';
import { useI18n } from '../../i18n';
import { useAnnounce } from '../../ui/a11y';
import { Pill } from '../../ui/DataBits';
import { loc } from '../../lib/loc';
import { AdminFrame } from './AdminFrame';
import { errorText, fmtDate, rpc, type AppError } from './api';
import type { SchoolCtx } from './SchoolPage';
import { XsCell, type XsReport } from '../r3/common';
import { periodLabel, termLabel } from '../../lib/r3format';
import { WhatWorksTable, type WhatWorks } from '../head/HeadR3Pages';
import { TopicShares } from '../municipality/MunicipalityHome';

// Release 3 (0019) for the platform admin: the school's cross-school
// authorisation (support mode, on the school page) and the Insights page —
// municipality coordinators, What works runs, period reports (preview,
// publish, withdraw), THL reference values, question set validation.
// Zencera sees aggregates that cleared every floor; never one school.

// ── School page: cross-school authorisation ────────────────────────────────

type XsStatus = { authorised: boolean; on: string | null; signatory: string | null; withdrawn_at: string | null; counts: boolean; school_type: string };

export function CrossSchoolPanel(ctx: SchoolCtx) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const school = ctx.detail.school.id;
  const [s, setS] = useState<XsStatus | null>(null);
  const [on, setOn] = useState('');
  const [who, setWho] = useState('');
  const [err, setErr] = useState<AppError | null>(null);
  const load = useCallback(async () => {
    const r = await rpc<XsStatus>('platform_cross_school', { p_school_id: school });
    if (r.error) setErr(r.error);
    else setS(r.data);
  }, [school]);
  useEffect(() => {
    load();
  }, [load]);
  async function set(give: boolean) {
    setErr(null);
    const r = await rpc<XsStatus>('platform_set_cross_school', { p_school_id: school, p_on: give, p_on_date: give ? on || null : null, p_signatory: give ? who : null });
    if (r.error) return setErr(r.error);
    setS(r.data);
    announce(t['pa.saved']);
  }
  if (!s) return err ? <p className="error-text" role="alert">{errorText(t, err)}</p> : null;
  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>{t['r3.xs.title']}</h2>
      <p className="form-hint">{t['r3.xs.hint']}</p>
      <p>
        {s.authorised ? <Pill kind="good">{t['r3.xs.authorised'](fmtDate(locale, s.on), s.signatory ?? '')}</Pill> : <Pill kind="neutral">{t['r3.xs.notAuthorised']}</Pill>}{' '}
        {s.authorised && !s.counts && <span className="text-muted">{t['r3.xs.notCounting']}</span>}
      </p>
      {s.authorised ? (
        <button type="button" className="btn btn-ghost" onClick={() => set(false)}>{t['r3.xs.withdraw']}</button>
      ) : (
        <form className="form" onSubmit={(e: FormEvent) => { e.preventDefault(); set(true); }} noValidate>
          <div className="form-row">
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-on`}>{t['so.signedOn']}</label>
              <input id={`${id}-on`} className="field" type="date" value={on} onChange={(e) => setOn(e.target.value)} />
            </div>
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-who`}>{t['so.signatory']}</label>
              <input id={`${id}-who`} className="field" value={who} maxLength={120} onChange={(e) => setWho(e.target.value)} />
            </div>
          </div>
          {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
          <div className="form-actions"><button type="submit" className="btn btn-primary">{t['so.record']}</button></div>
        </form>
      )}
    </section>
  );
}

// ── Insights page ──────────────────────────────────────────────────────────

export function AdminInsightsPage() {
  const { t } = useI18n();
  return <AdminFrame section="insights" title={t['r3.ai.title']}>{() => <InsightsBody />}</AdminFrame>;
}

type Overview = { authorised_schools: number; min_country: number; min_municipality: number; municipalities: { code: string; name: string; authorised: number; coordinators: number }[]; quarters: { year: number; q: number }[] };

function InsightsBody() {
  const { t } = useI18n();
  const [o, setO] = useState<Overview | null>(null);
  const [err, setErr] = useState<AppError | null>(null);
  const load = useCallback(async () => {
    const r = await rpc<Overview>('platform_cross_school_overview');
    if (r.error) setErr(r.error);
    else setO(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  if (err) return <p className="notice notice-block" role="alert">{errorText(t, err)}</p>;
  if (!o) return <p role="status">{t.loading}</p>;
  return (
    <>
      <p className="notice notice-block">{t['r3.ai.intro'](o.authorised_schools, o.min_country, o.min_municipality)}</p>
      <PeriodReports o={o} />
      <WhatWorksAdmin />
      <div className="two-col two-col-even">
        <Coordinators o={o} reload={load} />
        <AuthorisedByMunicipality o={o} />
      </div>
      <div className="two-col two-col-even">
        <ThlValues o={o} />
        <QuestionValidation />
      </div>
    </>
  );
}

function AuthorisedByMunicipality({ o }: { o: Overview }) {
  const { t } = useI18n();
  const id = useId();
  const rows = o.municipalities.filter((m) => m.authorised > 0 || m.coordinators > 0);
  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>{t['r3.ai.byMuni']}</h2>
      {rows.length === 0 ? <p className="text-muted">{t['r3.ai.noneYet']}</p> : (
        <dl className="kv">
          {rows.map((m) => (
            <div key={m.code}><dt>{m.name}</dt><dd>{t['r3.schoolsN'](m.authorised)} · {t['r3.ai.coordN'](m.coordinators)}</dd></div>
          ))}
        </dl>
      )}
    </section>
  );
}

type Coord = { grant_id: string; name: string; email: string; municipality_code: string; municipality_name: string; since: string };

function Coordinators({ o, reload }: { o: Overview; reload: () => void }) {
  const { t, locale } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [list, setList] = useState<Coord[]>([]);
  const [email, setEmail] = useState('');
  const [muni, setMuni] = useState('');
  const [err, setErr] = useState<AppError | null>(null);
  const load = useCallback(async () => {
    const r = await rpc<Coord[]>('platform_coordinators');
    if (!r.error) setList(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  async function grant(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const r = await rpc('platform_grant_coordinator', { p_email: email, p_municipality: muni });
    if (r.error) return setErr(r.error);
    setEmail('');
    announce(t['pa.saved']);
    load();
    reload();
  }
  async function revoke(c: Coord) {
    const r = await rpc('platform_revoke_coordinator', { p_grant_id: c.grant_id });
    if (r.error) return setErr(r.error);
    announce(t['r3.ai.revoked']);
    load();
    reload();
  }
  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>{t['r3.ai.coordTitle']}</h2>
      <p className="form-hint">{t['r3.ai.coordHint']}</p>
      {list.length > 0 && (
        <ul className="sched-list">
          {list.map((c) => (
            <li key={c.grant_id}>
              <span><strong>{c.name}</strong> · {c.email} · {c.municipality_name} <span className="text-muted">· {fmtDate(locale, c.since)}</span></span>
              <button type="button" className="link-btn" onClick={() => revoke(c)}>{t['r3.ai.revoke']}<span className="visually-hidden"> – {c.name}</span></button>
            </li>
          ))}
        </ul>
      )}
      <form className="form" onSubmit={grant} noValidate>
        <div className="form-row">
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-e`}>{t['r3.ai.email']}</label>
            <input id={`${id}-e`} className="field" type="email" autoComplete="off" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-m`}>{t['r3.ai.muni']}</label>
            <div className="select-wrap">
              <select id={`${id}-m`} className="select" value={muni} onChange={(e) => setMuni(e.target.value)}>
                <option value="">{t['r3.ai.choose']}</option>
                {o.municipalities.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
              </select>
            </div>
          </div>
        </div>
        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <div className="form-actions"><button type="submit" className="btn btn-primary" disabled={!email.includes('@') || !muni}>{t['r3.ai.grant']}</button></div>
      </form>
    </section>
  );
}

type Published = { id: string; level: string; code: string; area: string | null; period: string; title: Record<string, string>; is_draft: boolean; published_at: string; withdrawn_at: string | null };

function PeriodReports({ o }: { o: Overview }) {
  const { t, lang, locale } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [level, setLevel] = useState<'country' | 'municipality'>('country');
  const [code, setCode] = useState('');
  const [period, setPeriod] = useState(o.quarters[0] ? `${o.quarters[0].year}-${o.quarters[0].q}` : '');
  const [rep, setRep] = useState<XsReport | null>(null);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [pub, setPub] = useState<Published[]>([]);
  const [err, setErr] = useState<AppError | null>(null);
  const loadPub = useCallback(async () => {
    const r = await rpc<Published[]>('platform_published_reports');
    if (!r.error) setPub(r.data);
  }, []);
  useEffect(() => {
    loadPub();
  }, [loadPub]);
  const [y, q] = period.split('-').map(Number);
  async function preview(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    setRep(null);
    const r = await rpc<XsReport>('platform_period_report', { p_level: level, p_code: level === 'country' ? null : code, p_year: y, p_q: q });
    if (r.error) return setErr(r.error);
    setRep(r.data);
  }
  async function publish(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const r = await rpc('platform_publish_period_report', { p_level: level, p_code: level === 'country' ? null : code, p_year: y, p_q: q, p_title: { [lang]: title, en: title }, p_commentary: comment ? { [lang]: comment } : {} });
    if (r.error) return setErr(r.error);
    announce(t['r3.ai.published']);
    setTitle('');
    setComment('');
    loadPub();
  }
  async function withdraw(p: Published) {
    const r = await rpc('platform_withdraw_period_report', { p_id: p.id });
    if (r.error) return setErr(r.error);
    loadPub();
  }
  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>{t['r3.ai.reportsTitle']}</h2>
      <p className="form-hint">{t['r3.ai.reportsHint']}</p>
      <form className="form" onSubmit={preview} noValidate>
        <div className="form-row">
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-l`}>{t['r3.ai.level']}</label>
            <div className="select-wrap">
              <select id={`${id}-l`} className="select" value={level} onChange={(e) => { setLevel(e.target.value as 'country'); setRep(null); }}>
                <option value="country">{t['r3.ww.country']}</option>
                <option value="municipality">{t['r3.ai.muni']}</option>
              </select>
            </div>
          </div>
          {level === 'municipality' && (
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-c`}>{t['r3.ai.muni']}</label>
              <div className="select-wrap">
                <select id={`${id}-c`} className="select" value={code} onChange={(e) => { setCode(e.target.value); setRep(null); }}>
                  <option value="">{t['r3.ai.choose']}</option>
                  {o.municipalities.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
                </select>
              </div>
            </div>
          )}
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-p`}>{t['r3.mu.quarter']}</label>
            <div className="select-wrap">
              <select id={`${id}-p`} className="select" value={period} onChange={(e) => { setPeriod(e.target.value); setRep(null); }}>
                {o.quarters.map((x) => <option key={`${x.year}-${x.q}`} value={`${x.year}-${x.q}`}>{`Q${x.q} ${x.year}`}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="form-actions"><button type="submit" className="btn btn-ghost" disabled={!period || (level === 'municipality' && !code)}>{t['r3.ai.preview']}</button></div>
      </form>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      {rep && (
        <>
          <dl className="kv">
            <div><dt>{periodLabel(rep.period)}</dt><dd><XsCell w={rep.total} /></dd></div>
            {(['primary', 'lower_secondary', 'upper_secondary'] as const).map((k) => (
              <div key={k}><dt>{t[`r3.stage.${k}` as 'r3.stage.primary']}</dt><dd><XsCell w={rep.stages[k]} /></dd></div>
            ))}
          </dl>
          <TopicShares report={rep} />
          {rep.total.status === 'ok' && (
            <form className="form" onSubmit={publish} noValidate>
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-ti`}>{t['r3.ai.repTitle']}</label>
                <input id={`${id}-ti`} className="field" maxLength={140} value={title} onChange={(e) => setTitle(e.target.value)} />
              </div>
              <div className="field-group">
                <label className="field-label" htmlFor={`${id}-co`}>{t['r3.ai.repComment']}</label>
                <textarea id={`${id}-co`} className="field" rows={3} maxLength={2000} value={comment} onChange={(e) => setComment(e.target.value)} />
              </div>
              <p className="form-hint">{t['r3.ai.publishHint']}</p>
              <div className="form-actions"><button type="submit" className="btn btn-primary" disabled={title.trim().length < 3}>{t['r3.ai.publish']}</button></div>
            </form>
          )}
        </>
      )}
      {pub.length > 0 && (
        <>
          <h3 className="overline">{t['r3.ai.publishedList']}</h3>
          <ul className="sched-list">
            {pub.map((p) => (
              <li key={p.id}>
                <span>
                  <strong>{loc(p.title, lang)}</strong> · {p.area ?? t['r3.ww.country']} · {periodLabel(p.period)}{' '}
                  {p.withdrawn_at ? <Pill kind="neutral">{t['r3.ai.withdrawn']}</Pill> : <Pill kind="good">{t['r3.ai.public']}</Pill>}
                  <span className="text-muted"> · {fmtDate(locale, p.published_at)}</span>
                </span>
                {!p.withdrawn_at && <button type="button" className="link-btn" onClick={() => withdraw(p)}>{t['r3.ai.withdraw']}<span className="visually-hidden"> – {loc(p.title, lang)}</span></button>}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function WhatWorksAdmin() {
  const { t } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [ww, setWw] = useState<WhatWorks | null>(null);
  const [term, setTerm] = useState(lastCompleteTerm());
  const [err, setErr] = useState<AppError | null>(null);
  const load = useCallback(async () => {
    const r = await rpc<WhatWorks>('what_works');
    if (r.error) setErr(r.error);
    else setWw(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  async function compute(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const r = await rpc<{ rows: number }>('platform_compute_what_works', { p_term: term });
    if (r.error) return setErr(r.error);
    announce(t['r3.ai.computed'](r.data.rows));
    load();
  }
  return (
    <>
      <section className="panel p5" aria-labelledby={`${id}-t`}>
        <h2 className="panel-title" id={`${id}-t`}>{t['r3.ai.wwTitle']}</h2>
        <p className="form-hint">{t['r3.ai.wwHint']}</p>
        {ww && ww.computed_terms.length > 0 && <p className="text-muted">{t['r3.ai.computedTerms'](ww.computed_terms.map((x) => termLabel(t, x)).join(', '))}</p>}
        <form className="form" onSubmit={compute} noValidate>
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-term`}>{t['r3.ai.term']}</label>
            <input id={`${id}-term`} className="field" value={term} pattern="\d{4}-(spring|autumn)" aria-describedby={`${id}-th`} onChange={(e) => setTerm(e.target.value)} />
            <p className="form-hint" id={`${id}-th`}>{t['r3.ai.termHint']}</p>
          </div>
          {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
          <div className="form-actions"><button type="submit" className="btn btn-primary">{t['r3.ai.compute']}</button></div>
        </form>
      </section>
      {ww && <WhatWorksTable data={ww} />}
    </>
  );
}

function lastCompleteTerm(): string {
  const d = new Date();
  const y = d.getFullYear();
  return d.getMonth() >= 7 ? `${y}-spring` : `${y - 1}-autumn`;
}

function ThlValues({ o }: { o: Overview }) {
  const { t, lang } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [ind, setInd] = useState<{ key: string; label: Record<string, string> }[]>([]);
  const [f, setF] = useState({ key: '', level: 'country', code: '', stage: '8-9', year: String(new Date().getFullYear() - 1), value: '', source: '' });
  const [err, setErr] = useState<AppError | null>(null);
  useEffect(() => {
    rpc<{ indicators: { key: string; label: Record<string, string> }[] }>('thl_alignment').then((r) => !r.error && setInd(r.data.indicators));
  }, []);
  async function save(e: FormEvent) {
    e.preventDefault();
    setErr(null);
    const r = await rpc('platform_set_thl_value', { p_key: f.key, p_level: f.level, p_code: f.level === 'country' ? null : f.code, p_stage: f.stage, p_year: Number(f.year), p_value: Number(f.value.replace(',', '.')), p_source: f.source });
    if (r.error) return setErr(r.error);
    announce(t['pa.saved']);
    setF({ ...f, value: '' });
  }
  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>{t['r3.thl.title']}</h2>
      <p className="form-hint">{t['r3.ai.thlHint']}</p>
      <form className="form" onSubmit={save} noValidate>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-k`}>{t['r3.ai.indicator']}</label>
          <div className="select-wrap">
            <select id={`${id}-k`} className="select" value={f.key} onChange={(e) => setF({ ...f, key: e.target.value })}>
              <option value="">{t['r3.ai.choose']}</option>
              {ind.map((i) => <option key={i.key} value={i.key}>{loc(i.label, lang)}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-lv`}>{t['r3.ai.level']}</label>
            <div className="select-wrap">
              <select id={`${id}-lv`} className="select" value={f.level} onChange={(e) => setF({ ...f, level: e.target.value })}>
                <option value="country">{t['r3.ww.country']}</option>
                <option value="municipality">{t['r3.ai.muni']}</option>
              </select>
            </div>
          </div>
          {f.level === 'municipality' && (
            <div className="field-group">
              <label className="field-label" htmlFor={`${id}-mc`}>{t['r3.ai.muni']}</label>
              <div className="select-wrap">
                <select id={`${id}-mc`} className="select" value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })}>
                  <option value="">{t['r3.ai.choose']}</option>
                  {o.municipalities.map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
                </select>
              </div>
            </div>
          )}
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-st`}>{t['r3.ai.stage']}</label>
            <div className="select-wrap">
              <select id={`${id}-st`} className="select" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}>
                {['4-5', '8-9', 'lukio', 'amm'].map((x) => <option key={x} value={x}>{t[`r3.thlStage.${x}` as 'r3.thlStage.lukio']}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div className="form-row">
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-y`}>{t['r3.ai.year']}</label>
            <input id={`${id}-y`} className="field" inputMode="numeric" value={f.year} onChange={(e) => setF({ ...f, year: e.target.value })} />
          </div>
          <div className="field-group">
            <label className="field-label" htmlFor={`${id}-v`}>{t['r3.ai.value']}</label>
            <input id={`${id}-v`} className="field" inputMode="decimal" value={f.value} onChange={(e) => setF({ ...f, value: e.target.value })} />
          </div>
        </div>
        <div className="field-group">
          <label className="field-label" htmlFor={`${id}-src`}>{t['r3.ai.source']}</label>
          <input id={`${id}-src`} className="field" maxLength={300} value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} />
        </div>
        {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
        <div className="form-actions"><button type="submit" className="btn btn-primary" disabled={!f.key || !f.value || f.source.trim().length < 3}>{t['pa.save']}</button></div>
      </form>
    </section>
  );
}

type QSet = { version: number; label: string; topics: string[]; is_draft: boolean; validation_status: 'draft' | 'pilot_tested' | 'validated'; validation_note: Record<string, string> };

function QuestionValidation() {
  const { t, lang } = useI18n();
  const announce = useAnnounce();
  const id = useId();
  const [sets, setSets] = useState<QSet[]>([]);
  const [err, setErr] = useState<AppError | null>(null);
  const [note, setNote] = useState<Record<number, string>>({});
  const load = useCallback(async () => {
    const r = await rpc<QSet[]>('platform_question_sets');
    if (r.error) setErr(r.error);
    else setSets(r.data);
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  async function set(v: number, status: string) {
    setErr(null);
    const r = await rpc('platform_set_question_validation', { p_version: v, p_status: status, p_note: note[v] ? { [lang]: note[v] } : {} });
    if (r.error) return setErr(r.error);
    announce(t['pa.saved']);
    load();
  }
  return (
    <section className="panel p5" aria-labelledby={`${id}-t`}>
      <h2 className="panel-title" id={`${id}-t`}>{t['r3.qv.title']}</h2>
      <p className="form-hint">{t['r3.qv.hint']}</p>
      {err && <p className="error-text" role="alert">{errorText(t, err)}</p>}
      {sets.map((s) => (
        <div key={s.version} className="field-group">
          <p><strong>v{s.version} · {s.label}</strong> <Pill kind={s.validation_status === 'validated' ? 'good' : 'warn'}>{t[`r3.qv.${s.validation_status}` as 'r3.qv.draft']}</Pill></p>
          {loc(s.validation_note, lang) && <p className="text-muted">{loc(s.validation_note, lang)}</p>}
          <label className="field-label" htmlFor={`${id}-n${s.version}`}>{t['r3.qv.note']}</label>
          <input id={`${id}-n${s.version}`} className="field" maxLength={300} value={note[s.version] ?? ''} onChange={(e) => setNote({ ...note, [s.version]: e.target.value })} />
          <div className="chip-row">
            {(['draft', 'pilot_tested', 'validated'] as const).map((st) => (
              <button key={st} type="button" className="btn btn-ghost" aria-pressed={s.validation_status === st} onClick={() => set(s.version, st)}>{t[`r3.qv.${st}` as 'r3.qv.draft']}</button>
            ))}
          </div>
        </div>
      ))}
    </section>
  );
}
